import type { CSSProperties } from 'react'
import {
	Box,
	Button,
	Icon,
	IconButton,
	Input,
	initSpatialNav,
	Loading,
	initializeYakinikuCore,
	Textarea
} from '@yakiniku/core'
import { useState } from 'react'
import { createRoot } from 'react-dom/client'

import '../lib/base.sass'
// ^^^ in your app, you would import the compiled styles instead
// import '@yakiniku/core/base.css'
// import '@yakiniku/core/pixel.css' // optional

import pkg from '@pkg'
import {
	applyDemoVisualTheme,
	type DemoVisualTheme,
	getInitialDemoVisualTheme,
	isDemoVisualTheme
} from './demoTheme'
import iconSrc from './icon.gif'

const version = pkg.version
const initialDemoVisualTheme = getInitialDemoVisualTheme()
const accentColors = [
	{ name: 'Gray', value: '#8a8a8e' },
	{ name: 'Blue', value: '#7eb4ff' },
	{ name: 'Purple', value: '#caa6ff' },
	{ name: 'Pink', value: '#ff9bb2' },
	{ name: 'Orange', value: '#ffc36b' },
	{ name: 'Green', value: '#8ee6a3' }
]

interface AccentSwatchStyle extends CSSProperties {
	'--accent': string
}

const getAccentSwatchStyle = (
	accentValue: string,
	isSelected: boolean
): AccentSwatchStyle => ({
	'--accent': accentValue,
	backgroundColor: accentValue,
	borderColor: isSelected ? 'var(--text-focus)' : 'transparent',
	color: 'transparent',
	height: 'var(--control-height)',
	minWidth: 'var(--control-height)',
	padding: 0,
	width: 'var(--control-height)'
})

applyDemoVisualTheme(initialDemoVisualTheme)

const App = () => {
	const [visualTheme, setVisualTheme] = useState<DemoVisualTheme>(
		initialDemoVisualTheme
	)
	const [accent, setAccent] = useState(accentColors[0].value)

	const updateVisualTheme = (theme: DemoVisualTheme) => {
		applyDemoVisualTheme(theme)
		setVisualTheme(theme)
	}

	return (
		<div className="flex-center min-h-screen p-4">
			<div className="flex-col gap-5" style={{ maxWidth: 700 }}>
				<div className="flex-col gap-2 items-center mb-5">
					<h1>Yakiniku Core</h1>
					<em>
						v{version} -{' '}
						<a
							target="_blank"
							rel="noopener noreferrer"
							href="https://git.kyun.sh/yakiniku/core"
						>
							source code
						</a>
					</em>
				</div>
				<Box className="flex gap-4 flex-wrap items-center justify-center">
					<label className="flex gap-2 items-center">
						<span>Visual Theme</span>
						<select
							className="control"
							value={visualTheme}
							onChange={(event) => {
								const nextTheme = event.currentTarget.value

								if (isDemoVisualTheme(nextTheme)) {
									updateVisualTheme(nextTheme)
								}
							}}
						>
							<option value="pixel">Pixel</option>
							<option value="modern">Modern</option>
						</select>
					</label>
					<Button
						onClick={() =>
							document.documentElement.classList.toggle('light')
						}
					>
						Toggle Light
					</Button>
					<div
						className="flex gap-2 items-center"
						role="group"
						aria-label="Accent color"
					>
						{accentColors.map((color) => (
							<Button
								key={color.value}
								compact
								aria-label={`${color.name} accent`}
								aria-pressed={accent === color.value}
								title={`${color.name} accent`}
								style={getAccentSwatchStyle(
									color.value,
									accent === color.value
								)}
								onClick={() => {
									setAccent(color.value)
									document.documentElement.style.setProperty(
										'--accent',
										color.value
									)
								}}
							>
								{color.name}
							</Button>
						))}
					</div>
				</Box>
				<Box className="overflow-y-auto" style={{ maxHeight: 400 }}>
					<div className="prose">
						<h1>Heading One</h1>
						<h2>Heading Two</h2>
						<h3>Heading Three</h3>
						<p>
							Body text in the default font. Here is some{' '}
							<strong>bold text</strong>, <em>italic text</em>,
							and <code>inline code</code> styled with Gohu.
							Inline code should sit nicely within a line of body
							text.
						</p>
						<p>
							A second paragraph to show spacing. Links look like{' '}
							<a href="#">this</a>. Small text uses{' '}
							<small>a compact font</small>.
						</p>
						<h2>
							Heading with <code>code</code> inside
						</h2>
						<pre>{`pre block\n  indented\n    more indented`}</pre>
						<ul>
							<li>List item one</li>
							<li>
								List item two with <code>inline code</code>
							</li>
							<li>List item three</li>
						</ul>
					</div>
				</Box>

				<Box className="flex gap-4 justify-center">
					<em>Em</em>
					<span>Normal</span>
					<strong>Strong</strong>
					<small>Small</small>
				</Box>
				<Box className="flex flex-col gap-4 items-center justify-center">
					<em className="block max-w-100">
						HINT: Use .gif for icons. It is the most efficient
						format for small, low-color icons.
					</em>
					<div className="flex gap-4 items-center justify-center">
						<Icon src={iconSrc} width={8} />
						<IconButton aria-label="Example icon button">
							<Icon src={iconSrc} width={8} />
						</IconButton>
					</div>
				</Box>
				<Box className="text-center">
					<div className="flex gap-4 flex-wrap items-center justify-center">
						<Button
							onClick={() =>
								new Promise<void>((res) => {
									setTimeout(() => {
										res()
									}, 2000)
								})
							}
						>
							Wait
						</Button>
						<Button accented>Accent</Button>
						<Button danger>Danger</Button>
					</div>
				</Box>
				<Box className="flex-col gap-4">
					<div className="flex gap-4">
						<Input placeholder="Text input" />
						<Input
							type="number"
							placeholder="Number"
							suffix="GiB"
						/>
					</div>
					<Textarea placeholder="Textarea" rows={3} />
					<select className="control">
						<option>Option 1</option>
						<option>Option 2</option>
						<option>Option 3</option>
					</select>
				</Box>
				<Box className="h-50">
					<Loading />
				</Box>
			</div>
		</div>
	)
}

initializeYakinikuCore()
initSpatialNav()

const container = document.getElementById('app')
if (container) {
	const root = createRoot(container)
	root.render(<App />)
}
